import {
    SubmitHandler,
    useForm as useFormReactHookForm,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useOfferEventInitialValues } from 'offer/hooks/useOfferEventInitialValues';
import { TOfferEventFormValues, TOfferFormValues } from 'offer/types';
import {
    IOfferEventDetailFragment,
    IOfferEventType,
    IUpdateOfferEventInput,
} from 'graphql/types';
import { getImageId } from 'common/utils/image';
import { formatColor } from 'common/utils/color';
import { formatDescription } from 'offer/utils/offer';
import { getOfferFormValues } from 'offer/utils/forms';
import { getOfferEventFormValidator } from 'offer/validators/offerEventForm';

interface IProps {
    edit?: boolean;
    offerEvent?: IOfferEventDetailFragment;
    includeOfferFields?: boolean;
    offerEventType: IOfferEventType;
    isOfferDetailDrawer?: boolean;
    hasServiceProvider?: boolean;
    currentUserIsAuthor?: boolean;
    isOfferCategoryType: boolean;
    onSubmit(values: TOfferFormValues | IUpdateOfferEventInput): void;
}

export const useForm = ({
    edit,
    offerEvent,
    includeOfferFields,
    offerEventType,
    isOfferDetailDrawer,
    hasServiceProvider,
    currentUserIsAuthor,
    isOfferCategoryType,
    onSubmit,
}: IProps) => {
    const { initialValues, loading: loadingInitialValues } =
        useOfferEventInitialValues({
            edit,
            offerEvent,
            includeOfferFields,
            offerEventType,
            isOfferDetailDrawer,
            hasServiceProvider,
            currentUserIsAuthor,
        });

    const formMethods = useFormReactHookForm<TOfferEventFormValues>({
        defaultValues: initialValues,
        values: loadingInitialValues ? undefined : initialValues,
        // When `values` re-syncs, refresh untouched fields but keep anything
        // the user has already edited (prevents wiping in-progress changes).
        resetOptions: { keepDirtyValues: true },
        resolver: zodResolver(
            getOfferEventFormValidator(!!currentUserIsAuthor)
        ),
    });

    const {
        formState: { isSubmitting },
        control,
        handleSubmit,
    } = formMethods;

    const handleFormSubmit: SubmitHandler<TOfferEventFormValues> = (values) => {
        let formValues = {
            ...values,
            type: isOfferCategoryType ? values.type : offerEventType,
        };

        if (includeOfferFields) {
            const { overviewImage: currentOverviewImage, image: currentImage } =
                offerEvent?.offer || {};

            const offerFormValues = {
                overviewImageId: getImageId(
                    currentOverviewImage,
                    values.overviewImage
                ),
                titleColor: edit
                    ? undefined
                    : formatColor(values.titleColor || '') || null,
                imageId: edit
                    ? undefined
                    : getImageId(currentImage, values.image),
                description: edit
                    ? undefined
                    : formatDescription(values.description),
            };

            formValues = { ...formValues, ...offerFormValues };
        }

        const offerFormValues = getOfferFormValues(
            formValues,
            hasServiceProvider
        );

        return onSubmit(offerFormValues);
    };

    return {
        control,
        formMethods,
        loadingInitialValues,
        isSubmitting,
        handleFormSubmit: handleSubmit(handleFormSubmit),
    };
};
